<template>
  <!-- Renderless component: triggers Google One Tap prompt automatically -->
  <div />
</template>

<script>
/**
 * Shows the Google One Tap prompt (top-right overlay) automatically.
 *
 * Mount this component when:
 *   - The user is NOT logged in (login / register pages)
 *   - The user is logged in as a demo account
 *
 * When the user selects their Google account, the credential is dispatched
 * to the auth store which handles login/registration server-side.
 * For demo users this also logs them out of the demo session first.
 *
 * Does nothing when GOOGLE_CLIENT_ID is not configured (on-prem).
 */

import {
  GOOGLE_CLIENT_ID,
  initializeGoogle,
  loadScript,
  promptOneTap,
  setCredentialHandler
} from "../index";

export default {
  name: "GoogleOneTap",
  props: {
    repromptMs: {
      type: Number,
      default: 120000
    }
  },
  data: () => ({
    clientId: GOOGLE_CLIENT_ID,
    clearHandler: null,
    repromptInterval: null,
    oneTapDisabled: false
  }),
  computed: {
    isDemoSession() {
      return (
        this.$store.state.auth.user?.is_demo === true &&
        this.$store.getters["auth/isAuth"] === true
      );
    }
  },
  watch: {
    isDemoSession(isDemo) {
      if (isDemo) {
        this.startDemoRepromptLoop();
      } else {
        this.stopDemoRepromptLoop();
      }
    }
  },
  mounted() {
    if (!this.clientId) return;
    loadScript()
      .then(() => {
        initializeGoogle(this.clientId);
        this.clearHandler = setCredentialHandler(this.onCredentialResponse);
        this.showPrompt();
        this.startDemoRepromptLoop();
      })
      .catch(() => {}); // silently fail — button is the fallback
  },
  beforeDestroy() {
    this.stopDemoRepromptLoop();
    // Cancel pending One Tap prompt when navigating away
    if (this.clientId && window.google && window.google.accounts) {
      window.google.accounts.id.cancel();
    }
    if (this.clearHandler) {
      this.clearHandler();
      this.clearHandler = null;
    }
  },
  methods: {
    startDemoRepromptLoop() {
      if (!this.isDemoSession || this.repromptInterval) return;
      this.repromptInterval = setInterval(() => {
        if (!this.isDemoSession || document.hidden) return;
        this.showPrompt();
      }, this.repromptMs);
    },
    stopDemoRepromptLoop() {
      if (this.repromptInterval) {
        clearInterval(this.repromptInterval);
        this.repromptInterval = null;
      }
    },
    showPrompt() {
      if (this.oneTapDisabled) return;
      initializeGoogle(this.clientId);
      promptOneTap(notification => {
        if (notification?.isNotDisplayed?.() || notification?.isSkippedMoment?.()) {
          this.oneTapDisabled = true;
        }
      });
    },

    async onCredentialResponse(response) {
      this.stopDemoRepromptLoop();
      if (!response || !response.credential) return;

      const isDemoUser = this.$store.state.auth.user?.is_demo;

      // If currently logged in as demo, log out first
      if (isDemoUser) {
        try {
          await this.$store.dispatch("auth/logout");
        } catch {
          // continue anyway — the Google login will create a new session
        }
      }

      try {
        await this.$store.dispatch("auth/googleLogin", {
          credential: response.credential
        });
        this.$router.replace("/");
      } catch (error) {
        const detail = error.body?.detail || "Google login failed";
        this.$store.commit("raiseSnackbar", {
          text: detail,
          color: "error",
          timeout: 5000
        });
        this.startDemoRepromptLoop();
      }
    }
  }
};
</script>
